import { Suspense, lazy } from 'react';
import { UserVerification } from 'src/pages/auth/user-verification/UserVerification';
import { Switch, Route } from 'react-router-dom';
import OptOutForm from 'src/components/optOut/OptOutForm';
import { ResetPassword } from 'src/pages/auth/reset-password/ResetPassword';
import { Layout } from 'src/pages/auth/layout/Layout';
import { ChangePassword } from 'src/pages/auth/change-password/ChangePassword';
import { AuthenticatedLayout } from '@pages/auth/layout/AuthenticatedLayout';
import Oauth2Login from 'src/oauth2/newui/Oauth2Login';
import Oauth2ProtectedRoute from 'src/oauth2/newui/Oauth2ProtectedRoute';

const lazyComponentLoader = (importPath: () => Promise<{ default: React.ComponentType<any> }>) => {
  const LazyComponent = lazy(importPath);
  return LazyComponent;
};

const Contacts = lazyComponentLoader(() => import('@pages/audience/contacts/Contacts'));
const Channels = lazyComponentLoader(() => import('@pages/channels/Channels'));
const ViberConnectFlow = lazyComponentLoader(() => import('@pages/channels/viber/ViberConnectFlow'));
const Inbox = lazyComponentLoader(() => import('@pages/inbox/Inbox'));
const History = lazyComponentLoader(() => import('@pages/campaign/campaign-history/CampaignHistory'));
const Dashboard = lazyComponentLoader(() => import('@pages/dashboard/Dashboard'));
const Pricing = lazyComponentLoader(() => import('@pages/pricing/Pricing'));
const CustomerInvoices = lazyComponentLoader(() => import('@pages/invoices/Invoices'));
const Overview = lazyComponentLoader(() => import('@pages/analytics/overview/Overview'));
const CDRs = lazyComponentLoader(() => import('@pages/analytics/cdrs/CDRs'));
const Account = lazyComponentLoader(() => import('@pages/account/Account'));

const ContactsNew = lazyComponentLoader(() => import('../components/contacts/ContactsNew'));
const ContactsImport = lazyComponentLoader(() => import('../components/contacts/ContactsImport'));
const ContactsImportHistory = lazyComponentLoader(() => import('../components/contacts/ImportHistory'));
const Users = lazyComponentLoader(() => import('../components/administration/users'));
const Customers = lazyComponentLoader(() => import('../components/administration/customers'));
const AdminInvoices = lazyComponentLoader(() => import('../components/administration/finance'));
const PricelistTemplates = lazyComponentLoader(() => import('../components/administration/pricelistTemplates'));
const BalanceOverview = lazyComponentLoader(() => import('../components/administration/balanceOverview'));
const Tools = lazyComponentLoader(() => import('../components/administration/tools'));
const CampaignNew = lazyComponentLoader(() => import('../components/campaign/new'));
const NotFound = lazyComponentLoader(() => import('../components/NotFound'));
const Reseller = lazyComponentLoader(() => import('../components/administration/reseller'));
const Whatsapp = lazyComponentLoader(() => import('../components/administration/whatsapp'));
const DemoRequests = lazyComponentLoader(() => import('../components/administration/demoRequests'));
const Tokens = lazyComponentLoader(() => import('../components/account/tokens'));
const AdminEmail2SMS = lazyComponentLoader(() => import('../components/administration/email2sms'));
const MtApi = lazyComponentLoader(() => import('../components/administration/mtApi'));
const QuickCdrs = lazyComponentLoader(() => import('../components/cdrs'));
const InboundNumbers = lazyComponentLoader(() => import('../components/administration/inboundNumbers'));
const DailyReports = lazyComponentLoader(() => import('../components/reports/dailyReports'));
const TrafficReports = lazyComponentLoader(() => import('../components/reports/trafficReports'));
const ReportRecipients = lazyComponentLoader(() => import('../components/reports/reportRecipients'));
const QuickTestSms = lazyComponentLoader(() => import('../components/quickTestSms'));
const MobileNetworkNames = lazyComponentLoader(() => import('../components/mnp/mobileNetworkNames'));
const MobileCountryCodes = lazyComponentLoader(() => import('../components/mnp/mobileCountryCodes'));
const LookupHistory = lazyComponentLoader(() => import('../components/mnp/lookupHistory'));
const LookupNumber = lazyComponentLoader(() => import('../components/mnp/lookupNumber'));
const MccMncPricing = lazyComponentLoader(() => import('../components/mnp/mccMncPricing'));
const ResellerCosts = lazyComponentLoader(() => import('../components/settings/resellerCosts'));
const MasterDestinationList = lazyComponentLoader(() => import('../components/settings/masterDestination'));
const Tests = lazyComponentLoader(() => import('../components/tests/index'));
const UserFiles = lazyComponentLoader(() => import('../components/account/files'));
const Viber = lazyComponentLoader(() => import('../components/administration/viber'));

const basename = '';

export enum Routes {
  // TODO: add all routes
  ROOT = '/',
  LOGIN = '/login',
  ACCOUNT_RESET_PASSWORD = '/account/resetPassword',
  USER_VERIFY_HASH = '/user/verify/:hash',
  ACCOUNT_CHANGE_PASSWORD = '/account/changePassword',
  DASHBOARD = '/dashboard',
  CAMPAIGN_HISTORY = '/smsCampaign/history',
  CONTACTS_MANAGE = '/contacts/manage',
  ACCOUNT_PROFILE = '/account/profile',
  SMS_CHAT = '/smschat',
  CHANNELS = '/channels',
  CHANNELS_VIBER_CONNECT = '/channels/viber/connect',
  PRICING = '/finance/pricing',
  INVOICES = '/finance/invoices',
  ANALYTICS_OVERVIEW = '/finance/balance',
  ANALYTICS_CDRS = '/finance/cdrs',
  OPT_OUT = '/u/:hash',
}

export default function newUIRoutes() {
  return (
    <Switch>
      <Route
        path={[Routes.LOGIN, Routes.ACCOUNT_RESET_PASSWORD, Routes.USER_VERIFY_HASH, Routes.ACCOUNT_CHANGE_PASSWORD]}
        render={() => {
          return (
            <Layout>
              <Switch>
                <Route exact path={Routes.LOGIN} component={Oauth2Login} />
                <Route exact path={Routes.ACCOUNT_RESET_PASSWORD} component={ResetPassword} />
                <Route exact path={Routes.USER_VERIFY_HASH} component={UserVerification} />
                <Route exact path={Routes.ACCOUNT_CHANGE_PASSWORD} component={ChangePassword} />
              </Switch>
            </Layout>
          );
        }}
      />

      <Route exact path={Routes.OPT_OUT} component={OptOutForm} />

      <Route
        path={Routes.ROOT}
        render={() => {
          return (
            <AuthenticatedLayout>
              {/* TODO: Add loading component */}
              <Suspense fallback={<div>Loading...</div>}>
                <Switch>
                  <Oauth2ProtectedRoute exact isMui={false} path={Routes.ROOT} component={Dashboard} />
                  <Oauth2ProtectedRoute exact isMui={false} path={Routes.DASHBOARD} component={Dashboard} />
                  <Oauth2ProtectedRoute exact isMui={false} path={Routes.CAMPAIGN_HISTORY} component={History} />
                  <Oauth2ProtectedRoute exact isMui={false} path={Routes.CONTACTS_MANAGE} component={Contacts} />
                  <Oauth2ProtectedRoute exact isMui={false} path={Routes.CHANNELS_VIBER_CONNECT} component={ViberConnectFlow} />
                  <Oauth2ProtectedRoute exact isMui={false} path={Routes.CHANNELS} component={Channels} />
                  <Oauth2ProtectedRoute exact isMui={false} path={Routes.PRICING} component={Pricing} />
                  <Oauth2ProtectedRoute exact isMui={false} path={Routes.INVOICES} component={CustomerInvoices} />
                  <Oauth2ProtectedRoute exact isMui={false} path={Routes.ANALYTICS_OVERVIEW} component={Overview} />
                  <Oauth2ProtectedRoute exact isMui={false} path={Routes.ANALYTICS_CDRS} component={CDRs} />
                  <Oauth2ProtectedRoute
                    exact
                    isMui={false}
                    path={`${basename}${Routes.ACCOUNT_PROFILE}`}
                    component={Account}
                  />

                  <Oauth2ProtectedRoute exact path={`${basename}/smsCampaign/new`} component={CampaignNew} />
                  <Oauth2ProtectedRoute exact path={`${basename}/contacts/new`} component={ContactsNew} />
                  <Oauth2ProtectedRoute exact path={`${basename}/contacts/import`} component={ContactsImport} />
                  <Oauth2ProtectedRoute
                    exact
                    path={`${basename}/contacts/importHistory`}
                    component={ContactsImportHistory}
                  />
                  <Oauth2ProtectedRoute exact path={`${basename}/finance/invoices`} component={CustomerInvoices} />
                  <Oauth2ProtectedRoute exact path={`${basename}/account/tokens`} component={Tokens} />
                  <Oauth2ProtectedRoute exact path={`${basename}/administration/users`} component={Users} />
                  <Oauth2ProtectedRoute exact path={`${basename}/administration/customers`} component={Customers} />
                  <Oauth2ProtectedRoute exact path={`${basename}/administration/invoices`} component={AdminInvoices} />
                  <Oauth2ProtectedRoute exact path={`${basename}/administration/finance`} component={AdminInvoices} />
                  <Oauth2ProtectedRoute
                    exact
                    path={`${basename}/administration/pricelistTemplates`}
                    component={PricelistTemplates}
                  />
                  <Oauth2ProtectedRoute exact path={`${basename}/administration/balance`} component={BalanceOverview} />
                  <Oauth2ProtectedRoute exact path={`${basename}/administration/tools`} component={Tools} />
                  <Oauth2ProtectedRoute exact path={`${basename}/administration/reseller`} component={Reseller} />
                  <Oauth2ProtectedRoute exact path={`${basename}/admin/channels/whatsapp`} component={Whatsapp} />
                  <Oauth2ProtectedRoute exact path={`${basename}/administration/demoRequests`} component={DemoRequests} />
                  <Oauth2ProtectedRoute exact path={`${basename}/admin/channels/email2sms`} component={AdminEmail2SMS} />
                  <Oauth2ProtectedRoute exact path={`${basename}/admin/channels/mtApi`} component={MtApi} />
                  <Oauth2ProtectedRoute exact path={`${basename}/administration/cdrs`} component={QuickCdrs} />
                  <Oauth2ProtectedRoute
                    exact
                    path={`${basename}/administration/inboundNumbers`}
                    component={InboundNumbers}
                  />
                  <Oauth2ProtectedRoute exact path={Routes.SMS_CHAT} component={Inbox} isMui={false} />
                  <Oauth2ProtectedRoute exact path={`${basename}/reports/dailyReports`} component={DailyReports} />
                  <Oauth2ProtectedRoute exact path={`${basename}/reports/trafficReports`} component={TrafficReports} />
                  <Oauth2ProtectedRoute exact path={`${basename}/reports/reportRecipients`} component={ReportRecipients} />
                  <Oauth2ProtectedRoute exact path={`${basename}/quickTestSms`} component={QuickTestSms} />
                  <Oauth2ProtectedRoute
                    exact
                    path={`${basename}/mnp/mobileNetworkNames`}
                    component={MobileNetworkNames}
                  />
                  <Oauth2ProtectedRoute
                    exact
                    path={`${basename}/mnp/mobileCountryCodes`}
                    component={MobileCountryCodes}
                  />
                  <Oauth2ProtectedRoute exact path={`${basename}/mnp/lookupHistory`} component={LookupHistory} />
                  <Oauth2ProtectedRoute exact path={`${basename}/mnp/lookupNumber`} component={LookupNumber} />
                  <Oauth2ProtectedRoute exact path={`${basename}/mnp/mccMncPricing`} component={MccMncPricing} />
                  <Oauth2ProtectedRoute exact path={`${basename}/settings/resellerCosts`} component={ResellerCosts} />
                  <Oauth2ProtectedRoute
                    exact
                    path={`${basename}/settings/masterDestination`}
                    component={MasterDestinationList}
                  />
                  <Oauth2ProtectedRoute exact path={`${basename}/tests`} component={Tests} />
                  <Oauth2ProtectedRoute exact path={`${basename}/account/files`} component={UserFiles} />
                  <Oauth2ProtectedRoute exact path={`${basename}/admin/channels/viber`} component={Viber} />
                  <Oauth2ProtectedRoute path="*" component={NotFound} />
                </Switch>
              </Suspense>
            </AuthenticatedLayout>
          );
        }}
      />
    </Switch>
  );
}
